import { useMemo } from 'react';

import useShipmentsStore from './useShipmentsStore';
import shipmentsPostProcessor from '../helpers/shipmentsPostProcessor';
import { validationErrorParser } from '@/helpers/beErrorMsgParser';
import usePaginatedTableData from '@/hooks/usePaginatedTableData';

const parsingOptions = {
  errorParser: validationErrorParser,
  trackError: false,
};

type TSearchTermsOverride = {
  boxId?: string;
  partnerId?: string;
};

const useGetShipments = (searchTermsOverride?: TSearchTermsOverride) => {
  const dispatch = useShipmentsStore((state) => state.dispatch);
  const order = useShipmentsStore((state) => state.order);
  const sortBy = useShipmentsStore((state) => state.sortBy);
  const offset = useShipmentsStore((state) => state.offset);
  const pageSize = useShipmentsStore((state) => state.pageSize);
  const contractStatus = useShipmentsStore((state) => state.contractStatus);
  const rts = useShipmentsStore((state) => state.rts);
  const incidentFilter = useShipmentsStore((state) => state.incidentFilter);
  const searchTermsFromStore = useShipmentsStore((state) => state.searchTerms);
  const getAll = useShipmentsStore((state) => state.getAll);

  // Merge URL-param override (first render only) with store search terms.
  // This prevents a race condition: without it, two requests would fire —
  // one with the empty store and one after the store is updated from the URL —
  // and the first (unfiltered) response could overwrite the second (filtered) one.
  const searchTerms = useMemo(
    () =>
      searchTermsOverride
        ? { ...searchTermsFromStore, ...searchTermsOverride }
        : searchTermsFromStore,
    [searchTermsFromStore, searchTermsOverride]
  );

  const incident = (() => {
    if (!incidentFilter) {
      return {};
    }
    switch (incidentFilter[0]) {
      case 'FLAGGED':
        return { withLitigationStatuses: 'FLAGGED' };
      case 'SOLVED':
        return { withLitigationStatuses: 'SOLVED' };
      case 'INSPECTED':
        return { withSecurityInspections: true };
      default:
        return {};
    }
  })();

  usePaginatedTableData({
    params: {
      path: '/shipments',
      method: 'get',
      reducer: shipmentsPostProcessor,
    },
    dispatch,
    queryParams: {
      pageSize,
      offset,
      sortBy,
      order,
      getAll,
      ...(contractStatus && contractStatus.length > 0 && { contractStatus }),
      rts: rts || '',
      ...incident,
      ...searchTerms,
    },
    queryParamsOptions: { skipEmptyString: true },
    service: 'shipment',
    errorParsingOptions: parsingOptions,
  });
};

export default useGetShipments;
